import { useEffect, useMemo, useState } from "react";
import { Button, message, Pagination, PaginationProps, Row, Select, Space, Table, TableColumnsType, TableProps, Typography } from "antd";
import { BsPencilSquare } from "react-icons/bs";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { usePagination } from "src/hook/usePagination";
import {
  FillSamplingObject,
  FilterOthersFillEmission,
  OthersEmission,
  ResponseOthersFillSamplingResult,
  UpdateOthersFillEmission,
} from "src/entity/others/fill-emission";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { utilsStore } from "src/stores/utils-store";
import { othersStore } from "src/stores/others-store";
import { CalculationMethodOthersEnum, DISPLAY_SAMPLING_RESULT } from "src/entity/common/common.constant";
import { optionsSelectYears } from "src/utils/array";
import { useDidMount } from "src/hook/useDidMount";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { cloneDeep, isNull, omitBy, set } from "lodash";
import FillSamplingResultModal from "./FillSamplingResultModal";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";

const { Text } = Typography;
const calculationMethodStackSampling = CalculationMethodOthersEnum.STACK_SAMPLING;

const FillSamplingResultTable = (): React.ReactElement => {
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();

  const { permissions } = useNavigationMenu();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [visibleModal, setVisibleModal] = useState(false);
  const [dataToEdit, setDataToEdit] = useState<ResponseOthersFillSamplingResult[] | null>(null);
  const [dataEmission, setDataEmission] = useState<UpdateOthersFillEmission | {}>({});

  const profile = profileStore(useShallow((state) => state.profile));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));

  // Group 1. masterdata, utils data
  const getAllDistinctColFillEmission = othersStore(useShallow((state) => state.getAllDistinctColFillEmission));
  const dataFilterColFillEmission = othersStore(useShallow((state) => state.dataFilterColFillEmission));

  // Group 2. Main Service, main data
  const getOthersStackSampling = othersStore(useShallow((state) => state.getOthersStackSampling));
  const dataStackSampling = othersStore(useShallow((state) => state.dataStackSampling));
  const totalDocs = dataStackSampling.total;

  const updateOthersFillSamplingResult = othersStore(useShallow((state) => state.updateOthersFillSamplingResult));
  const loadingManageOthers = othersStore(useShallow((state) => state.loading.update));

  const clearError = othersStore(useShallow((state) => state.clearError));
  const errorFillEmission = othersStore(useShallow((state) => state.error));

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && dataAreaInCompany) {
      const optValue = dataAreaInCompany.find((item) => item._id === text);
      return optValue?.areaName;
    } else return text;
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    data: sourceData,
  } = usePagination<ResponseOthersFillSamplingResult, FilterOthersFillEmission>({
    action: getOthersStackSampling,
    remoteData: dataStackSampling.data,
    initialFilter: { year: [selectedYear], calculationMethod: calculationMethodStackSampling },
    defaultLimit: 5,
  });

  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);

  const areaFilter = useMemo(() => {
    if ((dataStackSampling.data?.length === 0 || dataFilterColFillEmission.area.length === 0) && currentFilterExcludePage == 0) return [];
    return dataFilterColFillEmission.area.map((item) => {
      const findLabel = dataAreaInCompany.find((e) => e._id === item);
      return {
        text: findLabel?.areaName,
        value: item,
      };
    });
  }, [dataFilterColFillEmission.area, dataStackSampling.data, dataAreaInCompany]);

  const sourceNameFilter = useMemo(() => {
    if (dataFilterColFillEmission.sourceName?.length === 0 && currentFilterExcludePage == 0) return [];
    return dataFilterColFillEmission.sourceName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [dataFilterColFillEmission.sourceName, dataStackSampling.data]);

  const emissionFilter = useMemo(() => {
    if ((dataStackSampling.data?.length === 0 || dataFilterColFillEmission.emission.length === 0) && currentFilterExcludePage == 0)
      return [];
    return dataFilterColFillEmission.emission.map((item) => {
      return {
        text: convertEmissionWord(item.toUpperCase()),
        value: item,
      };
    });
  }, [dataFilterColFillEmission.emission, dataStackSampling.data]);

  const samplingResultFilter = useMemo(() => {
    if (dataStackSampling.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return Object.entries(DISPLAY_SAMPLING_RESULT).map(([key, value]) => ({
      text: value,
      value: key,
    }));
  }, [dataStackSampling.data]);

  const getColumnFilter = () => {
    getAllDistinctColFillEmission(selectedYear, calculationMethodStackSampling);
  };

  const initialRequestDidMount = (isDidMount?: boolean) => {
    getAreaInCompany(profile?.activeCompany._id as string);
    getColumnFilter();
    const params =
      isDidMount === true
        ? { ...filter, page, limit: pageSize, calculationMethod: calculationMethodStackSampling }
        : { page, limit: pageSize, year: [selectedYear], calculationMethod: calculationMethodStackSampling };

    getOthersStackSampling(params);
  };

  useDidMount(() => {
    initialRequestDidMount(true);
  });

  const onChangePage = (val: number, size?: number) => {
    setPage(val);
    if (size && size !== pageSize) {
      onChange({
        ...filter,
        limit: size,
        page: 1,
      });
    }
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const closeModal = () => {
    setVisibleModal(false);
    clearError();
    setDataToEdit(null);
    setDataEmission({});
  };

  const onFinishAction = (response?: unknown) => {
    if (typeof response === "string") {
      return;
    } else {
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getColumnFilter();
      getOthersStackSampling({
        ...filter,
        page,
        limit: pageSize,
        year: [selectedYear],
        calculationMethod: CalculationMethodOthersEnum.FILL_EMISSION,
      });
      closeModal();
    }
  };

  const submitFillEmission = async () => {
    updateOthersFillSamplingResult(dataToEdit?.[0]._id as string, dataEmission as UpdateOthersFillEmission).then(onFinishAction);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;

  const columns: TableColumnsType<ResponseOthersFillSamplingResult> = [
    {
      title: "No",
      dataIndex: "no",
      width: 40,
      align: "center",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getIndex(Number(text))}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: "Source Name",
      dataIndex: "sourceName",
      key: "sourceName",
      width: 100,
      filters: !canViewTable(permissions) ? [] : sourceNameFilter,
      align: "center",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{text}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      width: 100,
      filters: !canViewTable(permissions) ? [] : areaFilter,
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getMasterLabel("area", text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: "Emission",
      dataIndex: "activeEmission",
      key: "emission",
      width: 100,
      filters: !canViewTable(permissions) ? [] : emissionFilter,
      align: "center",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{convertEmissionWord(text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (
          index === 0 ||
          (sourceData as any)[index - 1]._id !== record._id ||
          (sourceData as any)[index - 1].activeEmission !== record.activeEmission
        ) {
          obj.props.rowSpan = (sourceData as any).filter(
            (item: any) => item._id === record._id && item.activeEmission === record.activeEmission,
          ).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: "Sampling Result",
      dataIndex: "samplingResult",
      width: 100,
      filters: !canViewTable(permissions) ? [] : samplingResultFilter,
      align: "center",
      render: (text) => <Text>{DISPLAY_SAMPLING_RESULT[text as keyof typeof DISPLAY_SAMPLING_RESULT] ?? text}</Text>,
    },
    {
      title: "Unit",
      dataIndex: "unit",
      width: 60,
      render: (_, record) => <Text>{record.unit}</Text>,
      align: "left",
    },
    {
      title: "Emission",
      children: [
        { title: "Jan", dataIndex: "jan", key: "jan", width: 80, align: "center" },
        { title: "Feb", dataIndex: "feb", key: "feb", width: 80, align: "center" },
        { title: "Mar", dataIndex: "mar", key: "mar", width: 80, align: "center" },
        { title: "Apr", dataIndex: "apr", key: "apr", width: 80, align: "center" },
        { title: "May", dataIndex: "may", key: "may", width: 80, align: "center" },
        { title: "Jun", dataIndex: "jun", key: "jun", width: 80, align: "center" },
        { title: "Jul", dataIndex: "jul", key: "jul", width: 80, align: "center" },
        { title: "Aug", dataIndex: "aug", key: "aug", width: 80, align: "center" },
        { title: "Sep", dataIndex: "sep", key: "sep", width: 80, align: "center" },
        { title: "Oct", dataIndex: "oct", key: "oct", width: 80, align: "center" },
        { title: "Nov", dataIndex: "nov", key: "nov", width: 80, align: "center" },
        { title: "Dec", dataIndex: "dec", key: "dec", width: 80, align: "center" },
      ],
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (text, record, index) => {
        const obj = {
          children: (
            <Space size="middle" direction={"horizontal"}>
              <Button
                className="border-transparent bg-transparent"
                size={"small"}
                onClick={() => onClickEdit(record)}
                disabled={!canEditTable(permissions)}
              >
                <BsPencilSquare size={18} />
              </Button>
            </Space>
          ),
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1].no !== record.no) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item.no === record.no).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
  ];

  const handleChangeYear = (year: string) => {
    onChange({
      ...filter,
      year: [year],
      calculationMethod: calculationMethodStackSampling,
    });
    setSelectedYear(year);
  };

  const handleTableChange: TableProps<ResponseOthersFillSamplingResult>["onChange"] = (_, filters) => {
    onChange({
      ...filters,
      calculationMethod: calculationMethodStackSampling,
    });
  };

  const onClickEdit = (item: ResponseOthersFillSamplingResult) => {
    const id = item._id;
    const dataUpdate = sourceData?.filter((item) => String(item._id) === String(id));
    console.log({ item, id, dataUpdate });
    setDataToEdit(dataUpdate ?? null);
    setDataEmission({ emission: dataUpdate?.[0]?.emission ?? {} });
    setVisibleModal(true);
  };

  const onUpdateKeyValue = (activeEmission: keyof OthersEmission, samplingResult: string, value: string | number, month: string) => {
    const tmpDataEmission = cloneDeep(dataEmission) as UpdateOthersFillEmission;

    // Update nested emission data
    const emissionKey = activeEmission.toLowerCase() as keyof OthersEmission;
    const samplingKey = samplingResult as keyof FillSamplingObject;

    if (tmpDataEmission.emission?.[emissionKey]?.fillSamplingResult?.[samplingKey]) {
      // Update the nested value
      (tmpDataEmission.emission[emissionKey].fillSamplingResult[samplingKey] as any)[month] = value;

      console.log("Updated emission data:", tmpDataEmission.emission);

      // Update dataEmission state
      setDataEmission((prevState) => ({
        ...prevState,
        emission: tmpDataEmission.emission,
      }));
    }
  };

  return (
    <Row className="w-full" id={"wrap-fill-emission-table"}>
      {contextHolder}
      <Row className="w-full" justify={"space-between"}>
        <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
      </Row>
      <Table
        className="w-full mt-4"
        style={{ whiteSpace: "nowrap" }}
        rowKey={(rec, index) => `${rec._id}-${index}-${rec.no}`}
        columns={columns}
        dataSource={canViewTable(permissions) ? (sourceData as ResponseOthersFillSamplingResult[]) : []}
        scroll={{ x: "max-content" }}
        pagination={false}
        loading={loading}
        onChange={handleTableChange}
        // size="small"
      />
      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <FillSamplingResultModal
        dataEmission={dataEmission as UpdateOthersFillEmission}
        dataUpdate={dataToEdit}
        getMasterLabel={getMasterLabel}
        submitFillEmission={submitFillEmission}
        loadingManageFillEmission={loadingManageOthers}
        visibleModal={visibleModal}
        closeModal={closeModal}
        errorFillEmission={errorFillEmission}
        onUpdateKeyValue={onUpdateKeyValue}
      />
    </Row>
  );
};

export default FillSamplingResultTable;
